import { useState } from 'react';
import { content } from './content';
import { SelectField, TextField } from './fields';
import { formatPhone, isValidEmail, isValidPhone } from './validation';
const EMPTY = {
  facilityName: '',
  providerNumber: '',
  facilityAddress: '',
  name: '',
  phone: '',
  email: '',
  bestTime: '',
  questions: '',
};
function ContactRequestForm({ variant = 'facility', onSubmit }) {
  const c = content.contactRequest;
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [attempted, setAttempted] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const validate = (v) => {
    const e = {};
    const { errors: msgs } = content;
    if (variant === 'facility') {
      if (!v.facilityName.trim()) e.facilityName = msgs.required;
      if (!v.providerNumber.trim()) e.providerNumber = msgs.required;
      if (!v.facilityAddress.trim()) e.facilityAddress = msgs.required;
    }
    if (!v.name.trim()) e.name = msgs.required;
    if (v.phone.trim() && !isValidPhone(v.phone)) e.phone = msgs.phone;
    if (!v.email.trim()) e.email = msgs.required;
    else if (!isValidEmail(v.email)) e.email = msgs.email;
    if (!v.bestTime) e.bestTime = msgs.required;
    return e;
  };
  const set = (key, value) => {
    setValues((prev) => {
      const next = { ...prev, [key]: value };
      if (attempted) setErrors(validate(next));
      return next;
    });
  };
  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setAttempted(true);
    setSubmitError(null);
    const e = validate(values);
    setErrors(e);
    if (Object.keys(e).length) return;
    const request = {
      ...(variant === 'facility'
        ? {
            facilityName: values.facilityName.trim(),
            providerNumber: values.providerNumber.trim(),
            facilityAddress: values.facilityAddress.trim(),
          }
        : {}),
      name: values.name.trim(),
      phone: values.phone,
      email: values.email.trim(),
      bestTime: values.bestTime,
      questions: values.questions.trim(),
      submittedAt: /* @__PURE__ */ new Date().toISOString(),
    };
    try {
      setSending(true);
      await onSubmit(request);
      setSent(true);
    } catch (err) {
      setSubmitError(err instanceof Error && err.message ? err.message : content.errors.submitFailed);
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="tcf-contact">
      <h3 className="tcf-contact-title">{c.title}</h3>
      <p className="tcf-contact-intro">{variant === 'facility' ? c.facilityIntro : c.generalIntro}</p>
      {sent ? (
        <p className="tcf-contact-sent" role="status">
          {c.sent}
        </p>
      ) : (
        <form className="tcf-contact-form" onSubmit={handleSubmit} noValidate>
          {variant === 'facility' && (
            <>
              <div className="tcf-row">
                <TextField
                  label={c.labels.facilityName}
                  required
                  name="facilityName"
                  placeholder={c.placeholders.facilityName}
                  value={values.facilityName}
                  onChange={(e) => set('facilityName', e.target.value)}
                  error={errors.facilityName}
                />
                <TextField
                  label={c.labels.providerNumber}
                  required
                  name="providerNumber"
                  placeholder={c.placeholders.providerNumber}
                  value={values.providerNumber}
                  onChange={(e) => set('providerNumber', e.target.value)}
                  error={errors.providerNumber}
                />
              </div>
              <TextField
                label={c.labels.facilityAddress}
                required
                name="facilityAddress"
                placeholder={c.placeholders.facilityAddress}
                value={values.facilityAddress}
                onChange={(e) => set('facilityAddress', e.target.value)}
                error={errors.facilityAddress}
              />
            </>
          )}
          <div className="tcf-row">
            <TextField
              label={c.labels.name}
              required
              name="name"
              autoComplete="name"
              placeholder={c.placeholders.name}
              value={values.name}
              onChange={(e) => set('name', e.target.value)}
              error={errors.name}
            />
            <TextField
              label={c.labels.phone}
              type="tel"
              name="phone"
              autoComplete="tel"
              inputMode="tel"
              placeholder={c.placeholders.phone}
              value={values.phone}
              onChange={(e) => set('phone', formatPhone(e.target.value))}
              error={errors.phone}
            />
          </div>
          <div className="tcf-row">
            <TextField
              label={c.labels.email}
              required
              type="email"
              name="email"
              autoComplete="email"
              inputMode="email"
              placeholder={c.placeholders.email}
              value={values.email}
              onChange={(e) => set('email', e.target.value)}
              error={errors.email}
            />
            <SelectField
              label={c.labels.bestTime}
              required
              name="bestTime"
              options={[c.placeholders.bestTime, ...c.bestTimeOptions]}
              value={values.bestTime || c.placeholders.bestTime}
              onChange={(e) => set('bestTime', e.target.value === c.placeholders.bestTime ? '' : e.target.value)}
              error={errors.bestTime}
            />
          </div>
          <div className="tcf-field">
            <label className="tcf-label" htmlFor="tcf-contact-questions">
              {c.labels.questions}
            </label>
            <textarea
              id="tcf-contact-questions"
              className="tcf-input tcf-textarea"
              name="questions"
              rows={5}
              placeholder={c.placeholders.questions}
              value={values.questions}
              onChange={(e) => set('questions', e.target.value)}
            />
          </div>
          {submitError && (
            <p className="tcf-error tcf-error--submit" role="alert">
              {submitError}
            </p>
          )}
          <button type="submit" className="tcf-submit" disabled={sending}>
            {sending ? c.submitting : c.button}
          </button>
        </form>
      )}
    </div>
  );
}
export { ContactRequestForm };
